/**
 * CR-5880: load and edit the KPI graph milestones shown in
 * Admin Setup -> Performance -> KPI Milestones.
 */
import { useCallback, useEffect, useState } from 'react';
import { milestonesAPI, apiErrorMessage, type KpiMilestone, type KpiMilestoneInput } from '../../../lib/api';
import { toast } from 'sonner';

export interface UseKpiMilestones {
  milestones: KpiMilestone[];
  loading: boolean;
  /** True while a create / update / delete is in flight. */
  saving: boolean;
  /** @returns whether it was saved */
  create: (data: KpiMilestoneInput) => Promise<boolean>;
  /** @returns whether it was saved */
  update: (id: number, data: Partial<KpiMilestoneInput>) => Promise<boolean>;
  /** @returns whether it was deleted */
  remove: (id: number) => Promise<boolean>;
}

const byDate = (a: KpiMilestone, b: KpiMilestone) =>
  a.milestone_date.localeCompare(b.milestone_date) || a.id - b.id;

/**
 * The configuring company's milestones, oldest first, with create / update /
 * delete that report the server's own message on failure.
 *
 * @example
 * const { milestones, create } = useKpiMilestones();
 * await create({ title: 'ERP go-live', milestone_date: '2026-03-02', applies_to: 'both', is_active: true });
 *
 * @returns the list and the three writers
 */
export function useKpiMilestones(): UseKpiMilestones {
  const [milestones, setMilestones] = useState<KpiMilestone[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await milestonesAPI.list();
      const rows = Array.isArray(res.data) ? res.data : res.data.results ?? [];
      setMilestones([...rows].sort(byDate));
    } catch (err: unknown) {
      toast.error(apiErrorMessage(err, 'Could not load the milestones'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const run = useCallback(async (work: () => Promise<unknown>, done: string, failed: string) => {
    setSaving(true);
    try {
      await work();
      toast.success(done);
      await load();
      return true;
    } catch (err: unknown) {
      toast.error(apiErrorMessage(err, failed));
      return false;
    } finally {
      setSaving(false);
    }
  }, [load]);

  const create = useCallback((data: KpiMilestoneInput) =>
    run(() => milestonesAPI.create(data), 'Milestone added', 'Could not add the milestone'), [run]);
  const update = useCallback((id: number, data: Partial<KpiMilestoneInput>) =>
    run(() => milestonesAPI.update(id, data), 'Milestone saved', 'Could not save the milestone'), [run]);
  const remove = useCallback((id: number) =>
    run(() => milestonesAPI.remove(id), 'Milestone deleted', 'Could not delete the milestone'), [run]);

  return { milestones, loading, saving, create, update, remove };
}
